"use client";

import { NextIntlClientProvider, useLocale } from "next-intl";
import type { IntlError } from "next-intl";
import { usePostHog } from "posthog-js/react";
import type { ReactNode } from "react";
import { useCallback, useRef } from "react";

import { createBoundedDeduper } from "@/lib/bounded-dedupe";
import rootLogger from "@/lib/logging";

const logger = rootLogger.child({ module: "i18n/client" });

/**
 * How many distinct message errors one mounted provider reports to PostHog.
 * A page has far fewer distinct broken keys than this; the cap only exists so a
 * long-lived session cannot accumulate keys indefinitely.
 */
const MAX_REPORTED_ERRORS = 50;

/**
 * Adds error reporting to the surrounding next-intl configuration.
 *
 * next-intl resolves messages for Client Components against the nearest
 * provider, so their errors never reach the server `onError` in
 * `i18n/request.ts`. A Server Component cannot pass a function prop either,
 * which is why the provider that owns `onError` has to be a Client Component.
 *
 * It must therefore nest inside the `NextIntlClientProvider` that the localized
 * layout renders: that outer provider goes through next-intl's server adapter,
 * which resolves `timeZone` (and `formats`/`now`) from the request config.
 * `IntlProvider` falls back to the parent context for every value this provider
 * leaves undefined, so overriding only `onError` keeps that server-resolved
 * configuration intact. `locale` is the one exception — the client provider
 * requires it — so it is read back from the parent with `useLocale`.
 */
export function IntlErrorProvider({ children }: { children: ReactNode }) {
  const locale = useLocale();
  const posthog = usePostHog();
  // Scoped to the mounted provider so it lives as long as the messages it
  // reports on, and is dropped with them.
  const reportedRef = useRef<ReturnType<typeof createBoundedDeduper> | null>(
    null
  );

  const handleError = useCallback(
    (error: IntlError) => {
      // Pino keeps the browser path on Datadog + console and the server path on
      // stdout + OTel logs.
      logger.error(
        {
          // next-intl's code is the low-cardinality failure class; every
          // `IntlError` shares the same exception class.
          "error.type": error.code,
          "exception.type": error.name,
          "exception.message": error.message,
          "exception.stacktrace": error.stack,
        },
        "Internationalization error"
      );

      if (!posthog) return;

      // next-intl calls back on every render pass, so an unresolved message in a
      // component that rerenders per keystroke or slider tick would otherwise
      // flood Error Tracking with one exception per frame. Keys are only marked
      // once a client exists to capture them.
      reportedRef.current ??= createBoundedDeduper(MAX_REPORTED_ERRORS);
      if (!reportedRef.current.shouldReport(`${error.code}:${error.message}`)) {
        return;
      }

      posthog.captureException(error, {
        "error.type": error.code,
        "exception.type": error.name,
        "exception.message": error.message,
      });
    },
    [posthog]
  );

  return (
    <NextIntlClientProvider locale={locale} onError={handleError}>
      {children}
    </NextIntlClientProvider>
  );
}
